import React from "react";
import DOMPurify from "dompurify";

/** Parse inline markdown: bold, links */
export const parseInline = (text: string): (string | JSX.Element)[] => {
  const parts: (string | JSX.Element)[] = [];
  const regex = /(\*\*(.+?)\*\*|\[([^\]]+)\]\(([^)]+)\)|(?<![*\w])\*([^*\n]+)\*(?!\*)|`([^`\n]+)`)/g;
  let lastIndex = 0;
  let match;
  let key = 0;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    if (match[2]) {
      parts.push(<strong key={`b${key++}`} className="font-semibold">{parseInline(match[2])}</strong>);
    } else if (match[3] && match[4]) {
      const href = match[4];
      const isLiveChat = href === "#live-chat";
      const isExternal = href.startsWith("http");
      parts.push(
        <a
          key={`a${key++}`}
          href={isLiveChat ? undefined : href}
          className="text-primary hover:underline cursor-pointer"
          {...(isExternal ? { target: "_blank", rel: "noopener noreferrer" } : {})}
          {...(isLiveChat ? {
            onClick: (e: React.MouseEvent) => {
              e.preventDefault();
              window.dispatchEvent(new CustomEvent("open-live-chat"));
            }
          } : {})}
        >
          {match[3]}
        </a>
      );
    } else if (match[5]) {
      parts.push(<em key={`i${key++}`} className="italic">{parseInline(match[5])}</em>);
    } else if (match[6]) {
      parts.push(
        <code key={`c${key++}`} className="px-1.5 py-0.5 rounded bg-muted text-[0.9em] font-mono">
          {match[6]}
        </code>
      );
    }

    lastIndex = regex.lastIndex;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts;
};

/** Simple markdown renderer: headings, lists, tables, paragraphs */
export const renderBlogContent = (content: string) => {
  const lines = content.split("\n");
  const elements: JSX.Element[] = [];
  let key = 0;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (!line.trim()) continue;

    const imgMatch = line.match(/^!\[([^\]]*)\]\(([^)]+)\)$/);
    if (imgMatch) {
      const alt = imgMatch[1];
      const src = imgMatch[2];
      elements.push(
        <figure key={key++} className="my-6 sm:my-8 -mx-4 sm:mx-0">
          <img
            src={src}
            alt={alt}
            loading="lazy"
            className="w-full h-auto max-h-[70vh] object-cover sm:rounded-lg sm:border sm:border-border"
          />
          {alt && (
            <figcaption className="font-body text-xs sm:text-sm text-foreground/60 mt-2 px-4 sm:px-0 text-center italic">
              {alt}
            </figcaption>
          )}
        </figure>
      );
      continue;
    }

    // Code blocks (```...```)
    if (line.startsWith("```")) {
      const codeLines: string[] = [];
      i++;
      while (i < lines.length && !lines[i].startsWith("```")) {
        codeLines.push(lines[i]);
        i++;
      }
      elements.push(
        <pre key={key++} className="my-6 p-4 bg-muted rounded-lg overflow-x-auto text-sm font-mono border border-border">
          <code className="whitespace-pre">{codeLines.join("\n")}</code>
        </pre>
      );
      continue;
    }

    // Iframe embeds (raw HTML iframe on a single line)
    if (line.trim().startsWith("<iframe")) {
      const safe = DOMPurify.sanitize(line, {
        ALLOWED_TAGS: ["iframe"],
        ALLOWED_ATTR: ["src", "width", "height", "allowfullscreen", "title", "loading", "frameborder", "allow", "referrerpolicy"],
        ADD_ATTR: ["sandbox"],
      });
      // Force a sandbox attribute on the resulting iframe to limit any embed capabilities.
      const sandboxed = safe.replace(/<iframe\b(?![^>]*\bsandbox=)/i, '<iframe sandbox="allow-scripts allow-same-origin allow-presentation allow-popups"');
      elements.push(
        <div key={key++} className="my-6 relative w-full overflow-hidden rounded-lg border border-border" style={{ paddingBottom: "56.25%" }}>
          <div className="absolute inset-0 [&>iframe]:w-full [&>iframe]:h-full" dangerouslySetInnerHTML={{ __html: sandboxed }} />
        </div>
      );
      continue;
    }



    const t = line.trim();

    // Raw HTML headings/paragraphs occasionally present in imported content
    const htmlBlock = t.match(/^<(h[1-4]|p)\b[^>]*>([\s\S]*?)<\/\1>$/i);
    if (htmlBlock) {
      const tag = htmlBlock[1].toLowerCase();
      const inner = htmlBlock[2].replace(/<[^>]+>/g, "").trim();
      if (tag === "p") {
        elements.push(
          <p key={key++} className="font-body text-base leading-relaxed text-foreground/85 mb-4 break-words">
            {parseInline(inner)}
          </p>
        );
      } else if (tag === "h3" || tag === "h4") {
        elements.push(
          <h3 key={key++} className="font-display text-lg sm:text-xl font-semibold mt-6 sm:mt-8 mb-3 break-words">
            {parseInline(inner)}
          </h3>
        );
      } else {
        elements.push(
          <h2 key={key++} className="font-display text-xl sm:text-2xl font-bold mt-8 sm:mt-10 mb-3 sm:mb-4 break-words">
            {parseInline(inner)}
          </h2>
        );
      }
      continue;
    }

    // Horizontal rule
    if (/^(-{3,}|\*{3,}|_{3,})$/.test(t)) {
      elements.push(<hr key={key++} className="my-8 border-border" />);
      continue;
    }

    // Blockquote (supports consecutive lines)
    if (t.startsWith(">")) {
      const quoteLines: string[] = [t.replace(/^>\s?/, "")];
      while (i + 1 < lines.length && lines[i + 1].trim().startsWith(">")) {
        i++;
        quoteLines.push(lines[i].trim().replace(/^>\s?/, ""));
      }
      elements.push(
        <blockquote key={key++} className="my-6 border-l-4 border-primary/40 bg-primary/5 rounded-r-lg px-4 sm:px-5 py-3 sm:py-4 font-body text-base leading-relaxed text-foreground/85 break-words [&>p:last-child]:mb-0">
          {quoteLines.filter(Boolean).map((q, idx) => (
            <p key={idx} className="mb-2">{parseInline(q)}</p>
          ))}
        </blockquote>
      );
      continue;
    }

    if (t.startsWith("#### ")) {
      elements.push(
        <h4 key={key++} className="font-display text-base sm:text-lg font-semibold mt-5 sm:mt-6 mb-2 break-words">
          {parseInline(t.slice(5))}
        </h4>
      );
    } else if (t.startsWith("### ")) {
      elements.push(
        <h3 key={key++} className="font-display text-lg sm:text-xl font-semibold mt-6 sm:mt-8 mb-3 break-words">
          {parseInline(t.slice(4))}
        </h3>
      );
    } else if (t.startsWith("## ")) {
      elements.push(
        <h2 key={key++} className="font-display text-xl sm:text-2xl font-bold mt-8 sm:mt-10 mb-3 sm:mb-4 break-words">
          {parseInline(t.slice(3))}
        </h2>
      );
    } else if (t.startsWith("# ")) {
      elements.push(
        <h2 key={key++} className="font-display text-2xl sm:text-3xl font-bold mt-8 sm:mt-10 mb-3 sm:mb-4 break-words">
          {parseInline(t.slice(2))}
        </h2>
      );
    } else if (/^([-*•])\s+/.test(t)) {
      const strip = (s: string) => s.trim().replace(/^([-*•])\s+/, "");
      const items: string[] = [strip(t)];
      while (i + 1 < lines.length && /^([-*•])\s+/.test(lines[i + 1].trim())) {
        i++;
        items.push(strip(lines[i]));
      }
      elements.push(
        <ul key={key++} className="list-disc pl-5 sm:pl-6 space-y-1.5 mb-4 font-body text-base leading-relaxed text-foreground/85 break-words marker:text-primary/60">
          {items.map((item, idx) => (
            <li key={idx}>{parseInline(item)}</li>
          ))}
        </ul>
      );
    } else if (/^\d+[.)]\s+/.test(t)) {
      const strip = (s: string) => s.trim().replace(/^\d+[.)]\s+/, "");
      const items: string[] = [strip(t)];
      while (i + 1 < lines.length && /^\d+[.)]\s+/.test(lines[i + 1].trim())) {
        i++;
        items.push(strip(lines[i]));
      }
      elements.push(
        <ol key={key++} className="list-decimal pl-5 sm:pl-6 space-y-1.5 mb-4 font-body text-base leading-relaxed text-foreground/85 break-words marker:text-primary marker:font-semibold">
          {items.map((item, idx) => (
            <li key={idx} className="pl-1">{parseInline(item)}</li>
          ))}
        </ol>
      );
    } else if (/^\*\*[^*]+\*\*:?$/.test(t)) {
      elements.push(
        <p key={key++} className="font-display text-base sm:text-lg font-semibold mt-6 mb-2 break-words">
          {parseInline(t.replace(/^\*\*|\*\*:?$/g, ""))}
        </p>
      );
    } else if (line.startsWith("|") && line.endsWith("|")) {
      const tableLines: string[] = [line];
      while (i + 1 < lines.length && lines[i + 1].startsWith("|")) {
        i++;
        tableLines.push(lines[i]);
      }
      const dataRows = tableLines.filter(r => !r.match(/^\|[\s-|]+\|$/));
      if (dataRows.length > 0) {
        const headerCells = dataRows[0].split("|").filter(c => c.trim());
        const bodyRows = dataRows.slice(1);
        elements.push(
          <div key={key++} className="my-6 -mx-4 sm:mx-0 overflow-x-auto">
            <table className="w-full min-w-[480px] text-sm font-body border border-border sm:rounded-lg border-collapse">
              <thead>
                <tr className="bg-muted">
                  {headerCells.map((cell, idx) => (
                    <th key={idx} className="px-3 sm:px-4 py-2 text-left font-semibold whitespace-normal break-words align-top">{parseInline(cell.trim())}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {bodyRows.map((row, rIdx) => {
                  const cells = row.split("|").filter(c => c.trim());
                  return (
                    <tr key={rIdx} className="border-t border-border">
                      {cells.map((cell, cIdx) => (
                        <td key={cIdx} className="px-3 sm:px-4 py-2 whitespace-normal break-words align-top">{parseInline(cell.trim())}</td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      }
    } else {
      elements.push(
        <p key={key++} className="font-body text-base leading-relaxed text-foreground/85 mb-4 break-words">
          {parseInline(line)}
        </p>
      );
    }
  }
  return (
    <div className="blog-content max-w-full overflow-hidden [&_a]:break-words [&_img]:max-w-full [&_img]:h-auto">
      {elements}
    </div>
  );
};
